import { useState } from "react";

const imageCards = [
  {
    label: "Uncapped Earnings",
    sub: "Performance-driven. No salary ceiling.",
    img: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&q=80",
    imgAlt: "High-performance professional",
  },
  {
    label: "Elite Training",
    sub: "Learn IUL mechanics from CPAs, CIAs & engineers.",
    img: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&q=80",
    imgAlt: "Team training session",
  },
  {
    label: "Partnership Culture",
    sub: "High performers gain equity-level stake in the firm.",
    img: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80",
    imgAlt: "Business partners collaborating",
  },
];

export default function Careers() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", background: "" });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <main className="pt-20">
      {/* Hero */}
      <section className="relative py-32 px-6 overflow-hidden text-center">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1920&q=80"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-dark/95 via-navy/90 to-navy" />
        </div>
        <div className="relative max-w-2xl mx-auto">
          <p className="text-gold text-xs tracking-widest uppercase mb-4">Partner With Us</p>
          <h1 className="font-display text-5xl md:text-6xl font-bold text-white mb-4">
            Build Wealth.<br />
            <span className="text-gold">Build a Legacy.</span>
          </h1>
          <span className="gold-line mx-auto" />
          <p className="text-white/55 text-lg">
            High-performing professionals who want to engineer financial freedom for others — and build their own.
          </p>
        </div>
      </section>

      {/* 3 Image Cards */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
          {imageCards.map((c) => (
            <div key={c.label} className="group relative rounded-2xl overflow-hidden h-72 shadow-xl">
              <img
                src={c.img}
                alt={c.imgAlt}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/90 via-navy-dark/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="font-display text-xl font-bold text-white mb-1">{c.label}</h3>
                <p className="text-white/70 text-sm">{c.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Who We Want + Perks */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-gold text-xs tracking-widest uppercase mb-3">The Ideal Partner</p>
            <h2 className="section-title">Are You a Wealth Maximizer?</h2>
            <span className="gold-line" />
            <p className="text-gray-600 leading-relaxed mb-6">
              We attract professionals who are hungry, strategic, and ready to move beyond a salary ceiling.
            </p>
            <ul className="space-y-2 mb-8">
              {["Finance, banking, accounting", "Real estate and mortgage", "Healthcare and high-demand professions", "Entrepreneurship and sales", "Corporate management and consulting"].map((i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-gray-600">
                  <span className="text-gold mt-0.5">✦</span>{i}
                </li>
              ))}
            </ul>
            <p className="text-gray-400 text-xs">Life insurance license required (or willingness to obtain). Full licensing support provided.</p>
          </div>
          <div className="grid grid-cols-1 gap-4">
            {[
              { label: "Institutional Backing", desc: "Backed by AOG and Experior — not a solo contractor." },
              { label: "National Platform", desc: "Carrier relationships and client pipeline through our IMO network." },
              { label: "Proven System", desc: "High-conversion process built on education, not pressure." },
            ].map((p) => (
              <div key={p.label} className="card-dark">
                <h3 className="font-semibold text-navy mb-1">{p.label}</h3>
                <p className="text-gray-500 text-sm">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application form */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-gold text-xs tracking-widest uppercase mb-3">Apply Now</p>
            <h2 className="section-title">Express Your Interest</h2>
            <span className="gold-line mx-auto" />
          </div>

          {submitted ? (
            <div className="card-dark text-center py-16">
              <div className="text-4xl mb-4">✅</div>
              <h3 className="font-display text-2xl text-gold mb-2">Application Received</h3>
              <p className="text-gray-600">Our team will review your submission and reach out within 48 hours.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-500 text-xs tracking-widest uppercase mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={form.name}
                    onChange={handleChange}
                    className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-gray-800 text-sm focus:outline-none focus:border-gold transition-colors"
                    placeholder="Jane Smith"
                  />
                </div>
                <div>
                  <label className="block text-gray-500 text-xs tracking-widest uppercase mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-gray-800 text-sm focus:outline-none focus:border-gold transition-colors"
                    placeholder="jane@example.com"
                  />
                </div>
              </div>
              <div>
                <label className="block text-gray-500 text-xs tracking-widest uppercase mb-2">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-gray-800 text-sm focus:outline-none focus:border-gold transition-colors"
                  placeholder="+1 (555) 000-0000"
                />
              </div>
              <div>
                <label className="block text-gray-500 text-xs tracking-widest uppercase mb-2">Professional Background *</label>
                <textarea
                  name="background"
                  required
                  rows={3}
                  value={form.background}
                  onChange={handleChange}
                  className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-gray-800 text-sm focus:outline-none focus:border-gold transition-colors resize-none"
                  placeholder="Briefly describe your professional background..."
                />
              </div>
              <button type="submit" className="btn-gold w-full text-center py-4">
                Submit Application
              </button>
              <p className="text-gray-400 text-xs text-center">
                Submitting does not constitute an employment offer. A team member will contact you to discuss next steps.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* CTA image banner */}
      <section className="relative h-72 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=1920&q=80"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-navy-dark/80 flex items-center justify-center text-center px-6">
          <div>
            <h2 className="font-display text-3xl font-bold text-white mb-4">
              Your Future. <span className="text-gold">Engineered.</span>
            </h2>
            <a
              href="https://docs.google.com/forms/d/e/1FAIpQLSdcIec_5ByslvqXxOuuPF7U5ytk0-O-WGPq2UTYgipGwOeUOg/viewform?usp=publish-editor"
              target="_blank"
              rel="noreferrer"
              className="btn-gold"
            >
              Book a Discovery Call
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
